import { useState } from 'react';
import type { TenantMembership } from '../types';
import { useTenant } from '../contexts/TenantContext';
import { hasNonInternalTenant, getNonInternalTenants } from '../utils/accessControl';

interface InternalOnlyForbiddenPageProps {
  memberships: TenantMembership[];
}

/**
 * Shown when user's current tenant is marked as internal-only.
 * Internal-only tenants cannot access /app/* routes.
 * Users can switch to another tenant or go to Platform Admin.
 */
export const InternalOnlyForbiddenPage = ({
  memberships,
}: InternalOnlyForbiddenPageProps) => {
  const { switchTenant } = useTenant();
  const [switchingTenantId, setSwitchingTenantId] = useState<string | null>(null);

  const nonInternalTenants = getNonInternalTenants(memberships);
  const canSwitchToNonInternal = hasNonInternalTenant(memberships);

  // Two *different* tenants can legitimately share a display name
  // (memberships are already deduped by tenant_id), so a bare name can't
  // disambiguate two "Switch to X" buttons. Mirrors the discriminator
  // added to the Login picker and manage's ForbiddenPage (EL-806) for the
  // same ambiguous membership shape.
  const nameCounts = new Map<string, number>();
  nonInternalTenants.forEach((t) =>
    nameCounts.set(t.tenantName, (nameCounts.get(t.tenantName) ?? 0) + 1)
  );

  const handleSwitchTenant = async (tenantId: string) => {
    setSwitchingTenantId(tenantId);
    try {
      await switchTenant(tenantId);
    } catch {
      setSwitchingTenantId(null);
    }
  };

  const handleGoToPlatformAdmin = () => {
    window.location.href = '/manage/';
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/session/logout', {
        method: 'POST',
        credentials: 'include',
      });
      localStorage.removeItem('elli-current-tenant-id');
      localStorage.removeItem('elli-user-profile');
      localStorage.removeItem('elli-sidebar-collapsed');
    } catch {
      // Ignore errors
    }
    window.location.href = '/';
  };

  return (
    <div className="standalone-page fixed inset-0 flex items-center justify-center bg-gradient-to-br from-[#f5f7fa] to-[#e4e8ed] px-6 py-10 z-[1000]">
      <div className="bg-card p-12 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.1)] max-w-[500px] w-full text-center">
        <div className="text-[64px] mb-6">🔒</div>
        <h1 className="font-display text-[28px] font-extrabold tracking-[-0.015em] text-foreground m-0">Internal Access Only</h1>
        <p className="text-muted-foreground mb-8 leading-relaxed text-base">
          This workspace is configured for internal use only and cannot access the main application.
          Please use Platform Admin or switch to a different workspace.
        </p>

        {/* Platform Admin Button */}
        <div className="mb-6">
          <button
            onClick={handleGoToPlatformAdmin}
            className="text-base font-semibold py-3.5 px-6 rounded-lg border-none cursor-pointer transition-all duration-200 ease-in-out bg-primary text-primary-foreground hover:bg-primary/85"
          >
            Go to Platform Admin
          </button>
        </div>

        {/* Switch to non-internal tenant options */}
        {canSwitchToNonInternal && (
          <>
            <div className="my-6 border-t border-border" />
            <p className="text-muted-foreground mb-3 text-sm">
              You can switch to one of your other workspaces:
            </p>
            <div className="flex flex-col gap-3">
              {nonInternalTenants.map((tenant) => {
                const isDuplicate = (nameCounts.get(tenant.tenantName) ?? 0) > 1;
                const switchLabel = isDuplicate
                  ? `Switch to ${tenant.tenantName} (#${tenant.tenantId.slice(-8)})`
                  : `Switch to ${tenant.tenantName}`;
                return (
                  <button
                    key={tenant.tenantId}
                    onClick={() => handleSwitchTenant(tenant.tenantId)}
                    disabled={switchingTenantId !== null}
                    className="text-sm font-medium py-3 px-6 rounded-lg border border-border cursor-pointer transition-all duration-200 ease-in-out bg-card text-foreground hover:bg-muted disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {switchingTenantId === tenant.tenantId ? 'Switching...' : switchLabel}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* Logout option */}
        <div className="mt-6 pt-6 border-t border-border flex flex-col gap-3">
          <button
            onClick={handleLogout}
            className="text-sm font-medium py-3 px-6 rounded-lg border border-border cursor-pointer transition-all duration-200 ease-in-out bg-card text-muted-foreground hover:bg-muted"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default InternalOnlyForbiddenPage;
